<template>
	<SearchListLayout
		ref="layout"
		class="d-search-page"
		v-bind="{
			intro,
			...$attrs,
			searchArea: {
				singular: 'resultat',
				plural: 'resultater',
			},
			searchConfig: {
				enableGroupedSearch: true,
				limit: {
					1: 3,
					2: 3,
					3: 3,
					4: 3,
					5: 3,
					6: 3, // don't know how many there will be
					7: 3,
				},
			},
			defaults: getDefaults({
				text: '',
				sections: '',
				areas: '',
				employees: '',
			}),
			hideOnNoSearch: true,
		}"
	>
		<template #customHeader="{ fieldsByName, action }">
			<div class="pt-xs/v">
				<h1
					v-if="intro.title"
					class="sr-only"
					v-html="intro.title"
				></h1>

				<div
					class="
						d-search-page__search-fields
						grid grid-cols-12
						gap-x-3xs/h gap-y-2xs/v
						pb-sm/v
					"
				>
					<TransitionExt
						v-if="fieldsByName.text"
						name="search-page__search-bar"
						duration="400"
						is-group
						class="col-span-12 flex justify-end items-center"
						:appear="!isFirstPage"
					>
						<SearchTextInput
							key="content"
							v-bind="{
								...fieldsByName.text,
								type: 'search',
							}"
							v-model="tempSearchValue.text"
							@keyup.native.enter="
								() => submitText(fieldsByName, action.submit)
							"
						>
							<BaseButton
								aria-label="Udfør søgning"
								class="
									d-search-page__button-in-search
									w-48
									>=768:w-52
									h-48
									>=768:h-52
									p-0
									rounded-full
								"
								@click="
									() =>
										submitText(fieldsByName, action.submit)
								"
							>
								<SvgIconSearch class="w-18 h-18" />
							</BaseButton>

							<BaseButton
								aria-label="Luk søgning"
								class="
									d-search-page__button-in-search
									bg-transparent
									hover:bg-theme-10
									border border-theme
									text-theme
									w-48
									>=768:w-52
									h-48
									>=768:h-52
									p-0
									rounded-full
								"
								@click="goBack"
							>
								<SvgIconClose class="w-14 h-14" />
							</BaseButton>
						</SearchTextInput>
					</TransitionExt>

					<!-- Row of dropdowns -->
					<div
						class="
							col-span-12
							grid
							>=768:contents
							grid-cols-12
							gap-8
							>=768:gap-x-3xs/h >=768:gap-y-2xs/v
						"
					>
						<SearchDropdown
							v-if="fieldsByName.sections"
							v-bind="fieldsByName.sections"
							v-model="fieldsByName.sections.value"
							class="col-span-12 >=768:col-span-6"
							:class="{
								'>=1200:col-span-4':
									fieldsByName.sections &&
									fieldsByName.areas &&
									fieldsByName.employees,
							}"
							@input="() => action.submit()"
						/>

						<SearchDropdown
							v-if="fieldsByName.areas"
							v-bind="fieldsByName.areas"
							v-model="fieldsByName.areas.value"
							class="col-span-12 >=768:col-span-6"
							:class="{
								'>=1200:col-span-4':
									fieldsByName.sections &&
									fieldsByName.areas &&
									fieldsByName.employees,
							}"
							show-icon
							@input="() => action.submit()"
						/>

						<SearchDropdown
							v-if="fieldsByName.employees"
							v-bind="fieldsByName.employees"
							v-model="fieldsByName.employees.value"
							class="col-span-12 >=768:col-span-6"
							:class="{
								'>=1200:col-span-4':
									fieldsByName.sections &&
									fieldsByName.areas &&
									fieldsByName.employees,
							}"
							@input="() => action.submit()"
						/>
					</div>
				</div>
			</div>
		</template>
	</SearchListLayout>
</template>

<script>
import SearchListLayout from '~/components/main/SearchListLayout';
import SearchTextInput from '~/components/main/SearchTextInput';
import SearchDropdown from '~/components/main/SearchDropdown';
import SvgIconSearch from '~/assets/svgs/icon-search.svg?inline';
import SvgIconClose from '~/assets/svgs/icon-close.svg?inline';

export default {
	name: 'MainSearchList',

	components: {
		SearchListLayout,
		SearchTextInput,
		SearchDropdown,
		SvgIconSearch,
		SvgIconClose,
	},
	inheritAttrs: false,

	props: {
		isFirstPage: {
			type: Boolean,
			default: true,
		},

		intro: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		const defaults = this.getDefaults();
		return {
			tempSearchValue: {
				text:
					'text' in this.$route.query
						? this.$route.query.text ?? ''
						: defaults.text ?? '',
			},
		};
	},

	computed: {
		backLink() {
			return (
				this.$route.params.from || this.$store.state.site?.url || '/'
			);
		},
	},

	methods: {
		getDefaults(obj = {}) {
			const { fields = [] } = this.$attrs.search || {};
			const newObj = fields
				.filter((field) => 'defaultValue' in field)
				.reduce((acc, field) => {
					acc[field.name] = field.defaultValue;
					return acc;
				}, obj);
			return { ...obj, ...newObj };
		},
		submitText(fieldsByName, submit) {
			let changed = false;

			if (
				'text' in fieldsByName &&
				fieldsByName.text.value !== this.tempSearchValue.text
			) {
				fieldsByName.text.value = this.tempSearchValue.text;
				changed = true;
			}
			if (
				'includePhrase' in fieldsByName &&
				fieldsByName.includePhrase.value !==
					this.tempSearchValue.includePhrase
			) {
				fieldsByName.includePhrase.value =
					this.tempSearchValue.includePhrase;
				changed = true;
			}
			if (
				'includeWords' in fieldsByName &&
				fieldsByName.includeWords.value !==
					this.tempSearchValue.includeWords
			) {
				fieldsByName.includeWords.value =
					this.tempSearchValue.includeWords;
				changed = true;
			}
			if (
				'excludeWords' in fieldsByName &&
				fieldsByName.excludeWords.value !==
					this.tempSearchValue.excludeWords
			) {
				fieldsByName.excludeWords.value =
					this.tempSearchValue.excludeWords;
				changed = true;
			}

			changed && submit();
		},

		goBack() {
			this.$router.push(this.backLink);
			if (this.$route.params.from) {
				this.$router.back();
			}
		},
	},
};
</script>

<style lang="postcss">
.t-search-page__search-bar-enter-active,
.t-search-page__search-bar-leave-active {
	transition: transform, opacity;
	transform-origin: 100% 50%;

	& .d-search-page__button-in-search {
		transition: transform 400ms;
	}
}
.t-search-page__search-bar-enter,
.t-search-page__search-bar-leave-to {
	transform: translateX(-72px) scaleX(0.04);

	& .d-search-page__button-in-search {
		transform: scaleX(2.5);
	}
}
</style>
